/**
 * Browser side of the session (spec 10 §5). The session cookie is HttpOnly;
 * the readable CSRF cookie exists exactly while a session does and is
 * echoed as `X-CSRF-Token` on every state-changing request. Over https the
 * API names it `__Host-ss_csrf`, on a plain-http development origin `ss_csrf`.
 */
export const CSRF_COOKIE = 'ss_csrf';
export const HOST_CSRF_COOKIE = `__Host-${CSRF_COOKIE}`;
export const CSRF_HEADER = 'X-CSRF-Token';

export function readCookie(name: string): string | undefined {
  const cookies = globalThis.document?.cookie ?? '';
  for (const part of cookies.split(';')) {
    const [key, ...value] = part.trim().split('=');
    if (key === name) return decodeURIComponent(value.join('='));
  }
  return undefined;
}

/** The CSRF token to echo: the `__Host-` cookie first, the plain development name as fallback. */
export function readCsrfToken(): string | undefined {
  return readCookie(HOST_CSRF_COOKIE) || readCookie(CSRF_COOKIE) || undefined;
}

/** A hint only: the server decides; a stale cookie ends in a 401 and a new sign-in. */
export function hasSession(): boolean {
  return Boolean(readCsrfToken());
}

/**
 * `return_to` must stay inside the app: a relative path, never `//host`, a
 * scheme or a backslash (spec 10 AC-10-020 applies the same rule server-side).
 */
export function safeReturnTo(value: string | null | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return '/';
  }
  return value;
}

export type SessionEvent = 'unauthenticated' | 'tenant-access-denied' | 'tenant-suspended';

export interface SessionFlags {
  /** A request answered `401`: the session is gone. */
  readonly unauthenticated: boolean;
  /** A request answered `403 tenant-access-denied`: the active tenant is gone. */
  readonly tenantDenied: boolean;
  /** A request answered `403 tenant-suspended`: Bennows suspended the active tenant (spec 140 REQ-140-021). */
  readonly tenantSuspended: boolean;
}

const NO_FLAGS: SessionFlags = { unauthenticated: false, tenantDenied: false, tenantSuspended: false };
const FLAG_OF: Record<SessionEvent, keyof SessionFlags> = {
  unauthenticated: 'unauthenticated',
  'tenant-access-denied': 'tenantDenied',
  'tenant-suspended': 'tenantSuspended',
};

const changes = new EventTarget();
let flags: SessionFlags = NO_FLAGS;

/**
 * Raised by the API client for every response (spec 10 §6). Kept as state,
 * not as a one-off event, so a response that arrives before the session gate
 * subscribed is not lost.
 */
export function emitSessionEvent(event: SessionEvent): void {
  flags = { ...flags, [FLAG_OF[event]]: true };
  changes.dispatchEvent(new Event('change'));
}

/** After a successful sign-in the previous session's failures no longer apply. */
export function resetSessionFlags(): void {
  flags = NO_FLAGS;
  changes.dispatchEvent(new Event('change'));
}

export function subscribeSessionFlags(listener: () => void): () => void {
  changes.addEventListener('change', listener);
  return () => changes.removeEventListener('change', listener);
}

export function getSessionFlags(): SessionFlags {
  return flags;
}
